import { useEffect, useRef } from "react";
import { Button, Modal } from "@a2z/ui";

import { endpoints } from "@/lib/api/endpoints";
import type { KitchenChitView } from "@/lib/api/types";
import { useTranslate } from "@/lib/i18n";
import { useErrorMessage } from "@/shared/errors";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { usePrivateToast } from "@/shared/usePrivateToast";

import { reprintNeedsQuestion, type ReprintReason } from "../lib/reprintChit";

/**
 * «طباعة التذكرة» ON THE TILL, AFTER THE FIRST PAPER (D172, T14).
 *
 * A reprint after a jam has two meanings a cook must not confuse: the paper
 * never reached the kitchen (print it as the first one, or the food is never
 * made), or someone wants a second copy (mark it «نسخة مكررة», or the food is
 * made twice). Only the person at the till knows which, so it asks — once, and
 * only when the ticket has printed before. A ticket that never printed goes
 * straight through as NOT_RECEIVED with no question.
 *
 * Reads the ticket fresh (`printCount` moves when the kitchen's paper spools),
 * answers through `onAnswer` exactly once — with the `printCount` it read, so
 * the server can tell a person who was asked from a till that asked nobody
 * (a paper confirmed after this read was vouched for by no one, and its
 * reprint prints «نسخة مكررة») — and `onCancel` on a dismissal or a failed
 * read.
 */
export function ReprintQuestion({
  ticketId,
  onAnswer,
  onCancel,
}: {
  ticketId: string;
  onAnswer: (reason: ReprintReason, seenPrintCount: number) => void;
  onCancel: () => void;
}) {
  const t = useTranslate();
  const toast = usePrivateToast();
  const errorMessage = useErrorMessage();
  const answered = useRef(false);
  const { data, error } = useApiQuery<KitchenChitView>(
    queryKeys.kitchenTicket(ticketId),
    endpoints.restaurant.kitchenTicket(ticketId),
    { staleTime: 0, gcTime: 0, retry: false },
  );

  const answer = (reason: ReprintReason) => {
    if (answered.current || !data) return;
    answered.current = true;
    onAnswer(reason, data.printCount);
  };

  useEffect(() => {
    if (answered.current) return;
    if (error) {
      answered.current = true;
      toast.error(errorMessage(error));
      onCancel();
      return;
    }
    if (data && !reprintNeedsQuestion(data.printCount)) {
      answered.current = true;
      onAnswer("NOT_RECEIVED", data.printCount);
    }
  }, [data, error, errorMessage, onAnswer, onCancel, toast]);

  if (!data || !reprintNeedsQuestion(data.printCount)) {
    return null;
  }

  return (
    <Modal
      isOpen
      onClose={onCancel}
      size="sm"
      title={t((m) => m.reprintQuestion.title)}
      subtitle={t((m) => m.reprintQuestion.subtitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="reprint-question"
      footerActions={
        <>
          <Button variant="outline" data-testid="reprint-extra-copy" onClick={() => answer("EXTRA_COPY")}>
            {t((m) => m.reprintQuestion.extraCopy)}
          </Button>
          <Button variant="primary" data-testid="reprint-not-received" onClick={() => answer("NOT_RECEIVED")}>
            {t((m) => m.reprintQuestion.notReceived)}
          </Button>
        </>
      }
    >
      {/* What each answer prints — the difference a cook acts on. */}
      <dl className="flex flex-col gap-2 text-sm">
        <div>
          <dt className="font-bold">{t((m) => m.reprintQuestion.notReceived)}</dt>
          <dd className="text-[color:var(--text-muted)]">{t((m) => m.reprintQuestion.notReceivedHint)}</dd>
        </div>
        <div>
          <dt className="font-bold">{t((m) => m.reprintQuestion.extraCopy)}</dt>
          <dd className="text-[color:var(--text-muted)]">{t((m) => m.reprintQuestion.extraCopyHint)}</dd>
        </div>
      </dl>
    </Modal>
  );
}
